import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { useState } from 'react';
import type { FC } from 'react';
import type { InfiniteScrollItemsResult } from 'utils/react/useInfiniteScrollItems';
import './FacetedFilter.css';
import { FacetedFilterAttribute } from './FacetedFilterAttribute';
import type { FacetedFilterAttributeValueOption } from './FacetedFilterAttributeSelect';
import { FacetedFilterNewAttribute } from './FacetedFilterNewAttribute';
import type { FacetedFilterAttributeOption } from './FacetedFilterNewAttributeSelect';

export type FacetedFilterValue = {
  [attributeName: string]: string[];
};

interface Props {
  value: FacetedFilterValue;
  onChange: (value: FacetedFilterValue) => void;
  loadAttributes: (
    startIndex: number,
    filter: string,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeOption>>;
  makeLoadAttributeValues: (
    attribute: string,
  ) => (
    startIndex: number,
    filter: string,
    limit?: number,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeValueOption>>;
}

export const FacetedFilter: FC<Props> = (props) => {
  const [focusOnRender, setFocusOnRender] = useState<boolean>(false);

  const makeHandleDelete = (attributeName: string) => () => {
    setFocusOnRender(false);

    const mutableValue = { ...props.value };
    delete mutableValue[attributeName];

    props.onChange(mutableValue);
  };

  const makeHandleChangeValue =
    (attributeName: string) => (value: string[]) => {
      props.onChange({
        ...props.value,
        [attributeName]: value,
      });
    };

  const handleAttributeChange = (value: FacetedFilterValue) => {
    setFocusOnRender(true);

    props.onChange(value);
  };

  return (
    <div className='faceted-filter'>
      <div className='flex flex-wrap items-center'>
        {Object.entries(props.value).map(([attributeName, values]) => (
          <FacetedFilterAttribute
            key={attributeName}
            attributeName={attributeName}
            attributeValues={values}
            onDelete={makeHandleDelete(attributeName)}
            onChangeValue={makeHandleChangeValue(attributeName)}
            makeLoadAttributeValues={props.makeLoadAttributeValues}
            focusOnRender={focusOnRender}
          />
        ))}
        <FacetedFilterNewAttribute
          value={props.value}
          onChange={handleAttributeChange}
          loadAttributes={props.loadAttributes}
        />
      </div>
    </div>
  );
};
